import { Injectable } from '@angular/core';

import { Log } from '../../shared/log';

const log = new Log('widget-tab-override.service');

@Injectable()
export class WidgetTabOverrideService {
  widgets_list: Array<HTMLElement> = new Array();
  first_focusable_element: HTMLElement;
  focusable_element_names = 'a,button,select,input,textarea';
  widget_data_attribute = 'data-widget-order';
  tab_key = 9;

  // overrides the tab and shift tab event for widgets so that it navigates in the order of the masonry layout
  overrideTabForWidget(widgetElement: HTMLElement, index: number) {
    const self = this;

    // set widget order as data attribute
    widgetElement.setAttribute(this.widget_data_attribute, index.toString());

    // override the tab event
    widgetElement.addEventListener('keydown', (e: KeyboardEvent) => {
      // user has tabbed
      if (e.keyCode === self.tab_key) {
        // get a reference to the focusable element which the user pressed shift on
        const btn = (e.srcElement || e.target) as HTMLElement;

        // determine if the user is navigating backwards by using the shift key
        const backwards = e.shiftKey;

        // we need to determine if the button is the first button in a widget and if it's the last focusable element in the widget
        const widget_buttons = widgetElement.querySelectorAll(self.focusable_element_names);

        // find the index of the focusable element in list of all focusable elements for a widget
        let widget_button_index = 0;
        for (let x = 0; x < widget_buttons.length; x++) {
          if (widget_buttons[x] === btn) {
            widget_button_index = x;
          }
        }

        // use the index to determine if it's the first or last focusable element in the widget
        const is_last_button = widget_button_index === widget_buttons.length - 1;
        const is_first_button = widget_button_index === 0;

        // if the user isn't on the first or the last focusable element for a widget then don't override anything
        if (!is_last_button && !is_first_button) {
          return true;
        }

        // don't override anything if the user is on the first focusable element of multiple elements and moving forward
        if (is_first_button && !is_last_button && !backwards) {
          return true;
        }

        // don't override anything if the user is on the last focusable element of multiple elements and moving backwards
        if (is_last_button && !is_first_button && backwards) {
          return true;
        }

        // find widget parent and get the current index from the data attribute
        const el = self.getParentWidget(btn);
        let current_widget_index = +el.getAttribute(self.widget_data_attribute);

        // start searching for the focusable element for the next widget to focus
        let found_next_widget = false;
        let next_widget: HTMLElement;
        let next_widget_action: HTMLElement = null;

        while (found_next_widget === false) {
          // if the user has shift tabbed on the first element - just let it go back naturally
          if (current_widget_index === 0 && backwards) {
            return true;
          }

          // get the index of the next widget by either incrementing or decrementing the current index depending on if we're going backwards or forwards
          const next_widget_index = backwards ? current_widget_index - 1 : current_widget_index + 1;

          // find the next widget
          next_widget = document.querySelector(`[${self.widget_data_attribute}="${next_widget_index}"]`) as HTMLElement;

          // if there is a next widget, search the widget for focusable elements
          if (next_widget) {
            const next_widget_actions = next_widget.querySelectorAll(self.focusable_element_names);

            // if there are actions inside the widget then examine these
            if (next_widget_actions.length) {
              // if going forward, get the first action, otherwise get the last action
              if (backwards) {
                next_widget_action = next_widget_actions[next_widget_actions.length - 1] as HTMLElement;
              } else {
                next_widget_action = next_widget_actions[0] as HTMLElement;
              }

              found_next_widget = true;
            }
          }

          // if we've gotten to the end of the list of widgets and actions then we need to focus the next focusable element after the widget list manually
          if (next_widget_index === self.widgets_list.length) {
            next_widget_action = self.getFirstFocusableElementAfterWidgets();
            break;
          }

          // either increment or decrement depending on if we're going forward or backward
          if (backwards) {
            current_widget_index--;
          } else {
            current_widget_index++;
          }
        }

        // focus the next element if we have one
        if (next_widget_action) {

          if (backwards && next_widget_action.children[0].className === 'fa fa-archive dropdown-icon') {
            next_widget_action = next_widget_action.parentElement.parentElement;
          }

          next_widget_action.focus();

          // prevent standard browser tab behaviour
          e.preventDefault();
          return false;
        }
      }

      return true;
    });
  }

  // overrides the shift tab event for the first focusable element after the widget list
  // so that it focuses into the correct widget
  overrideShiftTabForFirstFocusableElementAfterWidgetList() {
    // once all the widgets are added, add a shift tab key override for the first element after all the widgets
    const first_focusable_element_after_widgets = this.getFirstFocusableElementAfterWidgets();

    if (first_focusable_element_after_widgets) {
      first_focusable_element_after_widgets.addEventListener('keydown',
        (e: KeyboardEvent) => {
          // only do stuff if the user is shift tabbing
          if (e.keyCode === this.tab_key && e.shiftKey) {
            // get the last widget with a focusable element and focus the last focusable element on that widget
            const last_widget = this.getLastWidgetWithFocusableElement();
            const last_widget_actions = last_widget.querySelectorAll(this.focusable_element_names);
            (last_widget_actions[last_widget_actions.length - 1] as HTMLElement).focus();

            // prevent default action
            e.preventDefault();
            return false;
          }

          return true;
        });
    }
  }

  // sets the list of widgets to be used in the service
  setWidgetsList(elements: Array<HTMLElement>) {
    // set the local list of elements
    this.widgets_list = elements;

    // if you're setting the data for the service you've probably just loaded the page so refresh this reference because it's probably changed
    this.first_focusable_element = null;
  }

  // returns the first focusable element after all the widgets
  // you need to manually focus into this element after tabbing from the last focusable widget element
  // you also need to override the shift tab event on this element to go back to the last widget
  getFirstFocusableElementAfterWidgets(): HTMLElement {
    // if you've already got this element referenced then just use the reference
    if (this.first_focusable_element) {
      return this.first_focusable_element;
    }

    // if you don't have the reference then start with finding the last widget with an action
    const all_widgets = document.querySelectorAll(`[${this.widget_data_attribute}]`);
    const last_widget = all_widgets[all_widgets.length - 1];

    // now get the last action of the last widget
    const last_widget_actions = last_widget.querySelectorAll(this.focusable_element_names);
    const last_widget_last_action = last_widget_actions[last_widget_actions.length - 1];

    // now get the index that this focusable element comes in the list of all focusable elements on the page
    const all_focusable_elements = document.querySelectorAll(this.focusable_element_names);

    for (let j = 0; j < all_focusable_elements.length; j++) {
      if (all_focusable_elements[j] === last_widget_last_action) {
        // if we've found the focusable element in this list then set it to be referenced later
        this.first_focusable_element = all_focusable_elements[j + 1] as HTMLElement;

        // now return that element
        return this.first_focusable_element;
      }
    }

    return null;
  }

  // finds the last widget with a focusable element
  // the tab event on the last focusable element on this widget needs to find the next element on the page after the widget list manually to focus
  getLastWidgetWithFocusableElement(): HTMLElement {
    // loop through all the widgets backwards
    for (let i = this.widgets_list.length - 1; i >= 0; i--) {
      const widget = this.widgets_list[i] as HTMLElement;

      // if there's anything focusable for the current widget then return this widget
      if (widget.querySelectorAll(this.focusable_element_names).length) {
        return widget;
      }
    }

    log.error('Could not find a widget with focusable elements');
    return null;
  }

  // gets the parent widget of an element
  getParentWidget(el: Element): Element {
    while (el) {
      if (el.hasAttribute(this.widget_data_attribute)) {
        break;
      }
      el = el.parentElement;
    }
    return el;
  }
}



// WEBPACK FOOTER //
// ./app/services/widget/widget-tab-override.service.ts